import { useMemo } from "react";
import { trpc, type RouterOutputs } from "../trpc";
import { useUserAccountRole } from "../permissions/useUserAccountRole";

type Guards = RouterOutputs["guard"]["getGuards"];

const EMPTY_GUARDS: never[] = [];

export const MANAGED_GUARDS_INPUT = {
  guardType: "managed_auth_only",
  isActive: true,
} as const;

// Guard selectedApis are versionless (e.g. "GoogleMailV2CLIAPI@1.0.0" ->
// "GoogleMailV2CLIAPI").
function stripVersion(appId: string) {
  return appId?.split("@")[0] ?? "";
}

function coversApp(guard: Guards[number], strippedId: string) {
  return (
    guard.selectedApis === "all" || guard.selectedApis.includes(strippedId)
  );
}

/** Mirrors the backend guard: per-app overrides and owner/super admin exemptions skip an operation. */
export function buildIsAppOperationRestricted(
  guards: Guards | undefined,
  operation: "create" | "reconnect" | "share",
  { isOwnerOrSuperAdmin = false }: { isOwnerOrSuperAdmin?: boolean } = {},
) {
  const list = guards ?? EMPTY_GUARDS;
  return (appId: string) => {
    const strippedId = stripVersion(appId);
    if (!strippedId) return false;
    return list.some((guard) => {
      if (!coversApp(guard, strippedId)) return false;
      if (guard.exemptOwnerAndSuperadmin && isOwnerOrSuperAdmin) return false;
      const operations = guard.perAppOperations?.[strippedId];
      return !operations || operations.includes(operation);
    });
  };
}

// Restrictions are per user (exemptions); isAppNonCompliant is per connection, like the
// backend's non-compliant filter. Callers also gate on the backend splits.
export function useQueryManagedGuards() {
  const { data, isLoading, isError } = trpc.guard.getGuards.useQuery(
    MANAGED_GUARDS_INPUT,
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  const { isAccountOwner, isSuperAdmin } = useUserAccountRole();
  const isOwnerOrSuperAdmin = isAccountOwner || isSuperAdmin;

  const isAppNonCompliant = useMemo(
    () => buildIsAppOperationRestricted(data, "create"),
    [data],
  );
  const isAppCreateRestricted = useMemo(
    () =>
      buildIsAppOperationRestricted(data, "create", { isOwnerOrSuperAdmin }),
    [data, isOwnerOrSuperAdmin],
  );
  const isAppReconnectRestricted = useMemo(
    () =>
      buildIsAppOperationRestricted(data, "reconnect", { isOwnerOrSuperAdmin }),
    [data, isOwnerOrSuperAdmin],
  );
  const isAppShareRestricted = useMemo(
    () => buildIsAppOperationRestricted(data, "share", { isOwnerOrSuperAdmin }),
    [data, isOwnerOrSuperAdmin],
  );

  return {
    isAppNonCompliant,
    isAppCreateRestricted,
    isAppReconnectRestricted,
    isAppShareRestricted,
    isLoading,
    isError,
  };
}
